import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IOCsService, IngestIOCPayload } from '../../services/iocsService';
import { IOCEntry, IOCsState, RootState } from '../../types';

const initialState: IOCsState = {
  iocs: [],
  isLoading: false,
  error: null,
};

export const fetchAllIOCs = createAsyncThunk(
  'iocs/fetchAllIOCs',
  async (params: { malicious_only?: boolean; is_blocked?: boolean; min_score?: number }, { rejectWithValue, getState }) => {
    try {
      let { clients } = (getState() as RootState).clients;
      if (clients.length === 0) {
        const { default: api } = await import('../../utils/axiosConfig');
        const response = await api.get('/clients');
        clients = response.data.clients || [];
      }
      if (clients.length === 0) return [];
      const results = await Promise.all(
        clients.map((client) => IOCsService.getIOCs({
          client_id: client.id,
          ...(params.min_score !== undefined && { min_score: params.min_score }),
          ...(params.malicious_only && { is_malicious: true }),
          ...(params.is_blocked !== undefined && { is_blocked: params.is_blocked }),
        }))
      );
      return results.flat();
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch IOCs');
    }
  }
);

export const ingestIOC = createAsyncThunk(
  'iocs/ingestIOC',
  async (payload: IngestIOCPayload, { rejectWithValue }) => {
    try {
      return await IOCsService.ingestIOC(payload);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to ingest IOC');
    }
  }
);

export const blockIOC = createAsyncThunk(
  'iocs/blockIOC',
  async ({ id, blockedUntil }: { id: string; blockedUntil?: string | null }, { rejectWithValue }) => {
    try {
      return await IOCsService.blockIOC(id, blockedUntil);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to block IOC');
    }
  }
);

export const unblockIOC = createAsyncThunk(
  'iocs/unblockIOC',
  async (id: string, { rejectWithValue }) => {
    try {
      return await IOCsService.unblockIOC(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to unblock IOC');
    }
  }
);

export const updateIOC = createAsyncThunk(
  'iocs/updateIOC',
  async ({ id, data }: { id: string; data: { notes?: string; tags?: string[] } }, { rejectWithValue }) => {
    try {
      return await IOCsService.updateIOC(id, data);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update IOC');
    }
  }
);

export const deleteIOC = createAsyncThunk(
  'iocs/deleteIOC',
  async (id: string, { rejectWithValue }) => {
    try {
      await IOCsService.deleteIOC(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete IOC');
    }
  }
);

const iocsSlice = createSlice({
  name: 'iocs',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllIOCs.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchAllIOCs.fulfilled, (state, action) => { state.isLoading = false; state.iocs = action.payload; })
      .addCase(fetchAllIOCs.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; })

      .addCase(ingestIOC.fulfilled, (state, action: PayloadAction<IOCEntry>) => {
        const idx = state.iocs.findIndex(i => i.id === action.payload.id);
        if (idx >= 0) state.iocs[idx] = action.payload;
        else state.iocs.unshift(action.payload);
      })

      .addCase(updateIOC.fulfilled, (state, action: PayloadAction<IOCEntry>) => {
        const idx = state.iocs.findIndex(i => i.id === action.payload.id);
        if (idx >= 0) state.iocs[idx] = action.payload;
      })

      .addCase(blockIOC.fulfilled, (state, action: PayloadAction<IOCEntry>) => {
        const idx = state.iocs.findIndex(i => i.id === action.payload.id);
        if (idx >= 0) state.iocs[idx] = action.payload;
      })
      .addCase(unblockIOC.fulfilled, (state, action: PayloadAction<IOCEntry>) => {
        const idx = state.iocs.findIndex(i => i.id === action.payload.id);
        if (idx >= 0) state.iocs[idx] = action.payload;
      })

      .addCase(deleteIOC.fulfilled, (state, action: PayloadAction<string>) => {
        state.iocs = state.iocs.filter(i => i.id !== action.payload);
      });
  },
});

export const { clearError } = iocsSlice.actions;
export default iocsSlice.reducer;
